import { useState, useEffect, useCallback } from 'react';
import { TopBar } from '../components/layout/TopBar';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { ExternalLink, Copy, Check, QrCode, Loader2, Save, Globe, Link, Instagram } from 'lucide-react';

const CHANNELS = [
  {
    key: 'google_reserve', name: 'Google Reserve', icon: '🔍', color: '#4285F4',
    desc: 'Aparece el botón "Reservar" en tu ficha de Google Maps y Búsqueda de Google',
    steps: ['Crea una cuenta en Google Business Profile', 'Activa la función Reserve with Google', 'Conecta OkReserva como plataforma de reservas'],
  },
  {
    key: 'facebook_bookings', name: 'Facebook Reservas', icon: '📘', color: '#1877F2',
    desc: 'Añade el botón "Reservar" a tu página de Facebook',
    steps: ['Ve a tu página de Facebook → Editar página → Botón de acción', 'Selecciona "Reservar" y elige "Otra plataforma"', 'Introduce tu URL de reservas de OkReserva'],
  },
  {
    key: 'instagram_bookings', name: 'Instagram Reservas', icon: '📸', color: '#E1306C',
    desc: 'Añade el enlace de reservas en tu bio y activa el botón de reserva',
    steps: ['Cambia a cuenta profesional en Instagram', 'Ve a Editar perfil → Botones de acción', 'Añade tu URL de reservas en el enlace de bio'],
  },
  {
    key: 'thefork', name: 'TheFork / LaFourchette', icon: '🍴', color: '#00A680',
    desc: 'Integración con la plataforma de reservas líder en Europa',
    steps: ['Regístrate en TheFork Manager', 'Activa la integración con OkReserva', 'Sincroniza disponibilidad y reservas automáticamente'],
  },
  {
    key: 'tripadvisor', name: 'TripAdvisor', icon: '🦉', color: '#34E0A1',
    desc: 'Reservas directas desde tu perfil de TripAdvisor',
    steps: ['Accede a TripAdvisor Business Hub', 'Añade el botón de reservas a tu perfil', 'Conecta con tu URL de reservas OkReserva'],
  },
];

function QRCode({ text, size = 120 }) {
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(text)}&bgcolor=ffffff&color=1e293b&format=png`;
  return <img src={qrUrl} alt="QR" className="rounded-lg" width={size} height={size} />;
}

export default function SocialBookings() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(null);
  const [expanded, setExpanded] = useState(null);

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    try {
      // El backend ya calcula y devuelve una booking_url canónica garantizada (apunta
      // a /widget/{restaurant_id} si la Página de Reservas no está publicada, o a
      // /b/{slug} si lo está). No necesitamos componer URLs en el cliente.
      const cfgR = await api.get('/social-bookings/config');
      setConfig(cfgR.data);
    } catch { toast.error('Error al cargar'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  const save = async () => {
    setSaving(true);
    try { await api.patch('/social-bookings/config', config); toast.success('Configuración guardada'); }
    catch { toast.error('Error al guardar'); }
    finally { setSaving(false); }
  };

  const copyUrl = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(text);
    setTimeout(() => setCopied(null), 2000);
    toast.success('Copiado');
  };

  const activeCount = CHANNELS.filter(c => config?.[c.key]).length;

  if (loading) return (
    <div className="h-screen flex flex-col"><TopBar title="Reservas desde Redes Sociales" />
      <div className="flex-1 flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-orange-500" /></div>
    </div>
  );

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <TopBar title="Reservas desde Redes Sociales" actions={
        <Button onClick={save} disabled={saving} className="bg-orange-500 hover:bg-orange-600 text-white h-8">
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Save className="h-3.5 w-3.5 mr-1" />} Guardar
        </Button>
      } />

      <div className="flex-1 p-6 max-w-5xl mx-auto w-full space-y-6">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center">
            <Instagram className="h-7 w-7 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Reservas desde Redes Sociales</h1>
            <p className="text-sm text-slate-500">Permite que tus clientes reserven desde Google, Instagram, Facebook y más</p>
          </div>
          <div className="ml-auto bg-orange-50 border border-orange-200 rounded-xl px-4 py-2.5 text-center">
            <div className="text-2xl font-black text-orange-600">{activeCount}</div>
            <div className="text-[10px] text-orange-500 font-medium">canales activos</div>
          </div>
        </div>

        {/* Booking URL */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-3"><CardTitle className="text-sm flex items-center gap-2"><Link className="h-4 w-4 text-orange-500" /> URL de reservas</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <p className="text-xs text-slate-500">Esta es la URL que debes usar en todos tus canales. Asegúrate de que tu Página de Reservas esté publicada.</p>
            <div className="flex items-center gap-2">
              <div className="flex-1 flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5">
                <Globe className="h-4 w-4 text-slate-400 flex-shrink-0" />
                <Input value={config?.booking_url || ''} onChange={e => setConfig(c => ({ ...c, booking_url: e.target.value }))}
                  className="border-0 bg-transparent p-0 text-sm font-mono text-slate-700 focus-visible:ring-0" />
              </div>
              <Button variant="outline" size="sm" onClick={() => copyUrl(config?.booking_url || '')} className="h-9 px-3">
                {copied === config?.booking_url ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
            <div className="flex items-start gap-6">
              <div>
                <p className="text-[11px] text-slate-400 mb-2">QR Code de reservas (imprímelo en menús, tarjetas, etc.)</p>
                {config?.booking_url && <QRCode text={config.booking_url} size={100} />}
              </div>
              <div className="flex-1">
                <p className="text-[11px] text-slate-400 mb-2">Usa este QR en:</p>
                <ul className="text-xs text-slate-500 space-y-1">
                  <li>• Menús del restaurante</li>
                  <li>• Tarjetas de visita</li>
                  <li>• Flyers y cartelería</li>
                  <li>• Firma de email</li>
                  <li>• Posts de Instagram Stories</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Channels */}
        <div className="space-y-3">
          {CHANNELS.map(ch => (
            <Card key={ch.key} className={cn("border-0 shadow-sm overflow-hidden transition-all", config?.[ch.key] ? "ring-2 ring-offset-1" : "")} style={config?.[ch.key] ? { ringColor: ch.color } : {}}>
              <CardContent className="p-0">
                <div className="p-4 flex items-center gap-4">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center text-xl flex-shrink-0" style={{ backgroundColor: ch.color + '20' }}>
                    {ch.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="text-sm font-semibold text-slate-800">{ch.name}</h3>
                      {config?.[ch.key] && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full font-bold" style={{ backgroundColor: ch.color + '20', color: ch.color }}>Activo</span>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5 truncate">{ch.desc}</p>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <button onClick={() => setExpanded(expanded === ch.key ? null : ch.key)}
                      className="text-xs text-slate-400 hover:text-slate-600 font-medium">
                      {expanded === ch.key ? 'Ocultar' : 'Ver pasos'}
                    </button>
                    <Switch checked={!!config?.[ch.key]} onCheckedChange={v => setConfig(c => ({ ...c, [ch.key]: v }))} />
                  </div>
                </div>
                {expanded === ch.key && (
                  <div className="px-4 pb-4 border-t border-slate-100 pt-3">
                    <p className="text-xs font-semibold text-slate-600 mb-2">Pasos para activar:</p>
                    <ol className="space-y-1.5">
                      {ch.steps.map((step, i) => (
                        <li key={i} className="flex items-start gap-2 text-xs text-slate-600">
                          <span className="w-4 h-4 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0 mt-0.5" style={{ backgroundColor: ch.color + '20', color: ch.color }}>{i+1}</span>
                          {step}
                        </li>
                      ))}
                    </ol>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}
